import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {compose} from "mu-lambda";
import * as React from "react";
import {Redirect} from "react-router-dom";

import {View} from "@khanacademy/wonder-blocks-core";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {PermissionsInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import type {
    forgotPassword,
    forgotPasswordVariables,
} from "./__graphql-types__/forgotPassword.ts";
import ForgotPasswordView, {type Phase} from "./forgot-password-view.tsx";
import {withStrings} from "./locales/forgot-password-page/load-strings.tsx";

type Props = WithLocationProps & WithApolloProps;

type State = {
    // `email` isn't included in `phase` since it's common between
    // all possible variants of Phase it can take and including it
    // makes this more complicated without a clear benefit.
    email: string;
    phase: Phase;
};

export const FORGOT_PASSWORD: GqlOperation<
    forgotPassword,
    forgotPasswordVariables
> = gqlOp(gql`
    mutation forgotPassword($email: String!) {
        forgotPassword(email: $email) {
            error {
                code
            }
            devOnlyLink
        }
    }
`);

// TODO(somewhatabstract): This could be simplified by using the `ApolloMutation`
// or `withApolloMutation` component, removing the need for state entirely.
class ForgotPasswordPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            phase: {
                id: "unsubmitted",
            },
            email: props.location.queryParams["login_email"] || "",
        };
    }

    handleReset: () => void = () => {
        const {email} = this.state;

        this.props.gqlClient
            .mutate(FORGOT_PASSWORD, {
                variables: {email},
            })
            .then((result) => {
                if (result.data) {
                    if (result.data.forgotPassword?.error) {
                        const {code} = result.data.forgotPassword.error;
                        this.setState({
                            phase: {
                                id: "error",
                                errorCode: code,
                            },
                        });
                    } else {
                        const devOnlyLink =
                            result.data.forgotPassword?.devOnlyLink;
                        this.setState({
                            phase: {
                                id: "success",
                                devOnlyLink: devOnlyLink,
                            },
                        });
                    }
                }
            })
            .catch((e) => {
                // Treat general errors the same as INTERNAL errors
                this.setState({
                    phase: {
                        id: "error",
                        errorCode: "INTERNAL",
                    },
                });
            });
    };

    render(): React.ReactNode {
        const {email, phase} = this.state;

        if (PermissionsInfo.isLoggedIn) {
            return <Redirect to="/" />;
        }

        return (
            <PageRoot>
                <View style={styles.container}>
                    <View style={styles.centered}>
                        <ForgotPasswordView
                            email={email}
                            phase={phase}
                            onUpdateEmail={(newEmail) =>
                                this.setState({email: newEmail})
                            }
                            onResetPassword={this.handleReset}
                        />
                    </View>
                </View>
            </PageRoot>
        );
    }
}

const PAGE_HEADER_HEIGHT = 60;

const styles = StyleSheet.create({
    container: {
        // Fill remaining height of the window so that the bottom of the container
        // lines up with the page fold.
        // NOTE: If there are urgent notifications, the bottom of the container
        // will be below the fold.
        minHeight: `calc(100vh - ${PAGE_HEADER_HEIGHT}px)`,
    },
    centered: {
        [mediaQueries.mdOrLarger]: {
            margin: "auto",
            // Shift the dialog up a bit so that it's vertically centered in the window
            transform: `translate(0px, -${PAGE_HEADER_HEIGHT / 2}x)`,
        },
        [mediaQueries.smOrSmaller]: {
            margin: 0,
        },
    },
});

type ExportProps = WithoutApollo<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof ForgotPasswordPage,
            React.ComponentProps<typeof ForgotPasswordPage>
        >
    >
>;

export default compose(
    withStrings,
    withLocation,
    withApollo,
)(ForgotPasswordPage) as React.ComponentType<ExportProps>;
